import React, { useEffect, useState } from "react";
import badgesBg from "../../../../assets/images/others/host-side-images/badgesBg.png";
import useAttendee from "containers/attendee/useHooks";
import { BAGDE_TYPES } from "utils/helpers/enums/shared.enum";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { useSelector } from "react-redux";
import badgesNotFoundGif from "assets/images/gifs/badge-not-found.gif";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import Pagination from "components/pagination";

class BadgeFiltersDTO {
  page: number = 0;
  size: number = 10;
  badgeType?: string = undefined;
}
const DigitalPassport: React.FC = () => {
  const [activeTab, setActiveTab] = useState("Learn Local’s Badges");
  const [data, setData] = useState<any>([]);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<BadgeFiltersDTO>(new BadgeFiltersDTO());
  const [totalElements, setTotalElements] = useState<number>(0);
  

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, data]);

  // Fetching Badges
  const { getMyBadges } = useAttendee();

  const fetchBadges = (filtersToUse?: BadgeFiltersDTO) => {
    const currentFilters = filtersToUse || filters;
    const queryParams: any = {};
    
    for (let key in currentFilters) {
      const value = currentFilters[key];
      // Include page and size always, include badgeType only if it has a value
      if (key === "page" || key === "size" || (value !== undefined && value !== "" && value !== null)) {
        queryParams[key] = value;
      }
    }

    if (activeTab === "Learn Local's Badges") {
      queryParams.badgeType = BAGDE_TYPES.ACHIEVEMENT;
    } else if (activeTab === "Event Badges") {
      queryParams.badgeType = BAGDE_TYPES.EVENT;
    }

    getMyBadges(setData, queryParams, setTotalElements);
    
    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  const handlePageChange = (pageInfo: { selected: number; limit: number }) => {
    const updatedFilters = {
      ...filters,
      page: pageInfo.selected,
      size: pageInfo.limit,
    };
    setFilters(updatedFilters);
    fetchBadges(updatedFilters);
  };

  useEffect(() => {
    // Reset filters when tab changes
    const resetFilters = new BadgeFiltersDTO();
    setFilters(resetFilters);
    fetchBadges(resetFilters);
  }, [activeTab]);

  return (
    <div className="min-h-screen">
      <h1 className="text-blackie text-3xl font-medium py-4">
        My Digital Passport
      </h1>
      {/* Tab Navigation */}
      <div className="flex justify-start mb-6">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-semibold ${
            activeTab === "Learn Local’s Badges"
              ? "text-lightBlueColor border-b-2 border-lightBlueColor"
              : "text-grayText"
          }`}
          onClick={() => setActiveTab("Learn Local’s Badges")}
        >
          Learn Local’s Badges
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-semibold ${
            activeTab === "Event Badges"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("Event Badges")}
        >
          Event Badges
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "Learn Local’s Badges" && (
          <>
            <div className="flex flex-wrap gap-4 py-5">
              {data?.map((badge: any) => (
                <div
                  key={badge.id}
                  className="flex flex-col items-center justify-center p-4 rounded-lg w-[235px] h-[260px]"
                  style={{ backgroundImage: `url(${badgesBg})` }}
                >
                  <div className="">
                    <img
                      src={concatPathWithBackendUrl(badge?.image?.name)}
                      alt="badge"
                      className="w-[150px] h-[150px] hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <span className="text-sm font-medium mt-2 text-blackie">
                    {badge.name}
                  </span>
                </div>
              ))}
            </div>
            <div>
              <CustomMessageDisplay
                show={!isLoading && !isInitialLoad && !data.length}
                title="No Learn Local Badges Earned"
                slogan="Complete experiences and activities to earn your first Learn Local badge!"
                image={badgesNotFoundGif}
                imageAlt="No badges found"
                className="py-16"
                imageClassName="w-[150px] h-[120px]"
                titleClassName="text-2xl font-bold text-blackie"
                sloganClassName="text-gray-500 text-base"
              />
            </div>
          </>
        )}

        {activeTab === "Event Badges" && (
          <>
            <div className="flex flex-wrap gap-4 py-5">
              {data?.map((badge: any) => (
                <div
                  key={badge.id}
                  className="flex flex-col items-center justify-center p-4 rounded-lg w-[235px] h-[260px]"
                  style={{ backgroundImage: `url(${badgesBg})` }}
                >
                  <div className="">
                    <img
                      src={concatPathWithBackendUrl(badge?.image?.name)}
                      alt="badge"
                      className="w-[150px] h-[150px] hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <span className="text-sm font-medium mt-2 text-blackie">
                    {badge.name}
                  </span>
                </div>
              ))}
            </div>
            <div>
              <CustomMessageDisplay
                show={!isLoading && !isInitialLoad && !data.length}
                title="No Event Badges Earned"
                slogan="Participate in events to earn your first event badge!"
                image={badgesNotFoundGif}
                imageAlt="No event badges found"
                className="py-16"
                imageClassName="w-[150px] h-[120px]"
                titleClassName="text-2xl font-bold text-blackie"
                sloganClassName="text-gray-500 text-base"
              />
            </div>
          </>
        )}
      </div>

      {data?.length > 0 && (
        <Pagination
          key={`pagination-${filters.page}-${filters.size}`}
          count={totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
    </div>
  );
};

export default DigitalPassport;
